'use client'

import type {
    HomeAIChatMessage,
    HomeAISearchContext,
    HomeAISearchRequest,
    HomeAISearchResponse
} from '@/src/types/homeAISearch'
import { homeAIMessages, resolveHomeAILocale } from '@/src/utils/homeAISearch/locale'
import { Box, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatedAISearchInput } from './AnimatedAISearchInput'
import { HomeAIPendingTripCard } from './HomeAIPendingTripCard'

export const AISearchBox = () => {
    const locale = useLocale()
    const homeAILocale = useMemo(() => resolveHomeAILocale(locale), [locale])
    const messages = useMemo(() => homeAIMessages[homeAILocale], [homeAILocale])
    const router = useRouter()
    const [inputValue, setInputValue] = useState('')
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [conversationContext, setConversationContext] = useState<HomeAISearchContext | undefined>(undefined)
    const [chatMessages, setChatMessages] = useState<HomeAIChatMessage[]>([
        { role: 'assistant', content: messages.greeting }
    ])
    const [pendingTrip, setPendingTrip] = useState<{
        searchQuery: NonNullable<HomeAISearchResponse['searchQuery']>
        filterQuery?: string
    } | null>(null)
    const chatScrollRef = useRef<HTMLDivElement>(null)
    const MAX_CONTEXT_HISTORY = 80

    useLayoutEffect(() => {
        const el = chatScrollRef.current
        if (el) {
            el.scrollTop = el.scrollHeight
        }
    }, [chatMessages])

    const navigateToSrp = (data: NonNullable<HomeAISearchResponse['searchQuery']>, filterQuery: string = '') => {
        const queryParams = new URLSearchParams()
        queryParams.set('from', data.from)
        queryParams.set('to', data.to)
        queryParams.set('adults', data.adults || '1')
        queryParams.set('childs', data.childs || '0')
        queryParams.set('infants', data.infants || '0')
        queryParams.set('class', data.class || 'Economy')
        queryParams.set('depart_date', data.depart_date)
        queryParams.set('return_date', data.return_date || '')

        if (data.nonstop) {
            queryParams.set('nonstop', data.nonstop)
        }


        queryParams.set('ai_filter_query', filterQuery)


        setConversationContext(undefined)
        setChatMessages([{ role: 'assistant', content: messages.greeting }])
        setInputValue('')
        setPendingTrip(null)

        router.push(`/${locale}/flights/results?${queryParams.toString()}`)
    }

    const submitSearch = async () => {
        const trimmed = inputValue.trim()
        if (!trimmed || loading) return

        const userChatMessage: HomeAIChatMessage = { role: 'user', content: trimmed }
        setChatMessages(prev => [...prev, userChatMessage])
        setInputValue('')
        setLoading(true)
        setErrorMessage(null)

        try {
            const nextHistory: HomeAIChatMessage[] = [...chatMessages, userChatMessage].slice(-MAX_CONTEXT_HISTORY)
            const body: HomeAISearchRequest = {
                userInput: trimmed,
                locale: homeAILocale,
                context: {
                    ...conversationContext,
                    chatHistory: nextHistory
                }
            }

            const response = await fetch('/api/ai-home-search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            })

            const data = (await response.json()) as HomeAISearchResponse

            if (response.status === 429) {
                setErrorMessage(messages.errors.RATE_LIMITED)
                return
            }

            if (!response.ok || data.status === 'error') {
                setErrorMessage(messages.errors.SOMETHING_WENT_WRONG)
                return
            }

            if (data.status === 'needs_clarification') {
                setPendingTrip(null)
                const assistantMessage = data.question || messages.fallbacks.clarificationDefault
                const assistantChatMessage: HomeAIChatMessage = { role: 'assistant', content: assistantMessage }
                setChatMessages(prev => [...prev, assistantChatMessage])
                setConversationContext({
                    partialSearchQuery: data.searchQuery,
                    previousMissingFields: data.missingFields,
                    previousQuestion: data.question,
                    chatHistory: [...nextHistory, assistantChatMessage].slice(-MAX_CONTEXT_HISTORY)
                })
                return
            }

            if (data.status === 'confirmed' && data.searchQuery) {
                navigateToSrp(data.searchQuery, data.filterQuery)
                return
            }

            if (data.status === 'ready' && data.searchQuery) {
                const assistantChatMessage: HomeAIChatMessage = {
                    role: 'assistant',
                    content: messages.fallbacks.reviewTripPrompt
                }
                setChatMessages(prev => [...prev, assistantChatMessage])
                setPendingTrip({
                    searchQuery: data.searchQuery,
                    filterQuery: data.filterQuery
                })
                setConversationContext(prev => ({
                    ...prev,
                    partialSearchQuery: data.searchQuery,
                    previousMissingFields: undefined,
                    previousQuestion: messages.fallbacks.reviewTripPrompt,
                    chatHistory: [...nextHistory, assistantChatMessage].slice(-MAX_CONTEXT_HISTORY)
                }))
            }
        } catch {
            setErrorMessage(messages.errors.NETWORK_ERROR)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box dir={homeAILocale === 'ar' ? 'rtl' : 'ltr'}>
            {chatMessages.length > 0 && (
                <Box
                    ref={chatScrollRef}
                    sx={{
                        mb: 1.25,
                        maxHeight: 180,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        }
                    }}
                >
                    {chatMessages.map((message, index) => (
                        <Box
                            key={`${message.role}-${index}`}
                            sx={{
                                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                            }}
                        >
                            <Typography variant="body2">{message.content}</Typography>
                        </Box>
                    ))}
                    {loading && (
                        <Box
                            sx={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: 'grey.100',
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            {[0, 1, 2].map(index => (
                                <Box
                                    key={`typing-dot-${index}`}
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: 'text.secondary',
                                        opacity: 0.35,
                                        animation: 'aiTypingWave 1s ease-in-out infinite',
                                        animationDelay: `${index * 0.15}s`,
                                        '@keyframes aiTypingWave': {
                                            '0%, 80%, 100%': {
                                                transform: 'translateY(0)',
                                                opacity: 0.3
                                            },
                                            '40%': {
                                                transform: 'translateY(-3px)',
                                                opacity: 1
                                            }
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {pendingTrip && (
                <HomeAIPendingTripCard
                    searchQuery={pendingTrip.searchQuery}
                    filterQuery={pendingTrip.filterQuery}
                    messages={messages}
                />
            )}


            <AnimatedAISearchInput
                showPlaceHolder={chatMessages?.length <= 1}
                messages={messages}
                inputValue={inputValue}
                loading={loading}
                onInputChange={setInputValue}
                onSubmit={submitSearch}
            />






            <Box>
                <Typography variant="body2" color='textDisabled' ml={3} mt={1}>
                    {messages.description}
                </Typography>
            </Box>

            {errorMessage && (
                <Box>
                    <Typography variant="body2" ml={3} mt={1} color='error'>
                        {errorMessage}
                    </Typography>
                </Box>
            )}

        </Box>
    )
}
